import type {
  AdminFeedbackItem,
  AdminFeedbackQueueFilters,
  AdminFeedbackQueueResponse,
  AdminFeedbackThreadResponse,
  FeedbackAttachmentInput,
  FeedbackCategory,
  FeedbackListResponse,
  FeedbackStatus,
  FeedbackThreadResponse,
  ProductFeedback,
  ProductFeedbackReply,
  SubmitFeedbackInput,
} from '@doneverse/shared'
import { api } from '../api/index.js'

export type FeedbackType = SubmitFeedbackInput['type']

/**
 * The three redesigned submission forms (brief §4.1). `label` is the ~140-char
 * one-liner that titles the item in both the submitter's list and the admin
 * queue; `impact` is the self-reported severity (bugs) or importance (feature
 * requests) and is absent for general feedback.
 */
export type SubmitFeedbackRequest = SubmitFeedbackInput

export interface FeedbackReplyRequest {
  body: string
  attachments?: FeedbackAttachmentInput[]
}

export interface AdminFeedbackReplyRequest extends FeedbackReplyRequest {
  /** True when the body came from the canned-response library. */
  is_canned?: boolean
  /**
   * Folds the brief's stacked triage actions into one request: acknowledge is a
   * canned reply plus `'acknowledged'`, and acknowledge-and-close — the common
   * path for feature requests and general feedback — is `'closed'`.
   */
  set_status?: FeedbackStatus
}

export interface FeedbackTriageInput {
  status?: FeedbackStatus
  category?: FeedbackCategory | null
  owner_id?: string | null
  clickup_task_url?: string | null
}

function buildQuery(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue
    const str = String(value)
    if (str.length === 0) continue
    search.set(key, str)
  }
  const str = search.toString()
  return str ? `?${str}` : ''
}

export const feedbackService = {
  submit: async (data: SubmitFeedbackRequest): Promise<ProductFeedback> => {
    return api.post(data, '/v1/product-feedback').json<ProductFeedback>()
  },

  /** The submitter's own items, newest first, with founder-facing statuses. */
  list: async (): Promise<FeedbackListResponse> => {
    return api.get('/v1/product-feedback').json<FeedbackListResponse>()
  },

  /** One own item with its thread. Someone else's item 404s. */
  getThread: async (id: string): Promise<FeedbackThreadResponse> => {
    return api.get(`/v1/product-feedback/${encodeURIComponent(id)}`).json<FeedbackThreadResponse>()
  },

  reply: async (id: string, data: FeedbackReplyRequest): Promise<ProductFeedbackReply> => {
    return api
      .post(data, `/v1/product-feedback/${encodeURIComponent(id)}/replies`)
      .json<ProductFeedbackReply>()
  },
}

export const adminFeedbackService = {
  /**
   * The Feedback Queue. The API sorts new-and-unassigned first, so an unfiltered
   * call already lands on the untriaged work (brief §5.1).
   */
  listQueue: async (filters: AdminFeedbackQueueFilters = {}): Promise<AdminFeedbackQueueResponse> => {
    const qs = buildQuery({
      search: filters.search,
      status: filters.status,
      category: filters.category,
      type: filters.type,
      submitter_role: filters.submitter_role,
      owner_id: filters.owner_id,
      impact: filters.impact,
      created_after: filters.created_after,
      created_before: filters.created_before,
      page: filters.page,
      limit: filters.limit,
    })
    return api.get(`/v1/admin/product-feedback${qs}`).json<AdminFeedbackQueueResponse>()
  },

  getThread: async (id: string): Promise<AdminFeedbackThreadResponse> => {
    return api.get(`/v1/admin/product-feedback/${encodeURIComponent(id)}`).json<AdminFeedbackThreadResponse>()
  },

  triage: async (id: string, patch: FeedbackTriageInput): Promise<AdminFeedbackItem> => {
    return api.patch(patch, `/v1/admin/product-feedback/${encodeURIComponent(id)}`).json<AdminFeedbackItem>()
  },

  reply: async (id: string, data: AdminFeedbackReplyRequest): Promise<ProductFeedbackReply> => {
    return api
      .post(data, `/v1/admin/product-feedback/${encodeURIComponent(id)}/replies`)
      .json<ProductFeedbackReply>()
  },
}
